import {
  Badge,
  Flex,
  FormatDate,
  Icon,
  NumberInput,
  OverlayTrigger,
  Popover,
  Spinner,
  Text,
} from '@wyz/ui';
import { DateUtils } from '@wyz/utils';
import { NumberField } from '~/common/components';

import { isNil } from 'lodash-es';
import { Trans, useI18n, useLocale } from '~/common/context';
import { MIN_QUANTITY } from '~/purchase/helpers';
import {
  IAcceptOrderValidationErrorResponse,
  ISuggestedOffer,
} from '~/purchase/types';
import { IOrder } from '@wyz/types';
type Props = {
  offer: Partial<ISuggestedOffer['offer']>;
  tyre: {
    priceFrame: {
      price: number | null;
    };
  };
  index: number;
  change?: IAcceptOrderValidationErrorResponse;
  supplier: {
    id: number;
    name: string;
  };
  oldValues?: {
    date?: Date;
    quantity?: number;
    sellingPrice?: number;
  };
  isProcessing?: boolean;
  isPartiallyAvailable?: boolean;
  order: IOrder;
};
function SuggestedTyreRow({
  isProcessing = false,
  offer,
  tyre,
  order,
  index: offerIndex,
  change,
  supplier,
  oldValues,
  isPartiallyAvailable,
}: Props) {
  const { t } = useI18n();
  const locale = useLocale();

  return (
    <tr
      className='bg-white shadow-none border-0 mt-3 w-25 mb-4'
      style={{
        opacity: offer.availableQuantity ? 1 : 0.5,
      }}
    >
      <td className='bg-white border-0 pt-4'>
        <Text
          heading='normal'
          weight='semi-bold'
          className='text-left bg-white'
          id={`supplier-${offerIndex}-${supplier.id}`}
        >
          {supplier.name}
          {!offer.availableQuantity && (
            <OverlayTrigger
              trigger={['focus', 'hover']}
              placement='bottom'
              overlay={
                <Popover className='py-1 px-2'>
                  <Trans i18nKey='app.purchase.redistribution.pending_order.accept.errors.unavailable_source' />
                </Popover>
              }
            >
              <Text component='span' color='danger'>
                <Icon icon='error-warning-line' height={18} width={18} />
              </Text>
            </OverlayTrigger>
          )}
        </Text>
      </td>
      {!isNil(tyre.priceFrame.price) && (
        <td className='bg-white border-0 pt-4'>
          <Text
            heading='normal'
            weight='semi-bold'
            className='text-center bg-white'
            id={`bf-${offerIndex}-${supplier.id}`}
          >
            {locale.formatters.money(tyre.priceFrame.price, {
              currency: offer.currencyIsoCode,
              fallback: '/',
            })}
          </Text>
        </td>
      )}

      <td className='bg-white border-0 pt-4'>
        <Text
          heading='normal'
          className='bg-white text-center'
          weight='semi-bold'
          color={
            offer.overriddenPriorityIfShorterDelivery ? 'success' : undefined
          }
        >
          {isProcessing ? (
            <Spinner style={{ width: 20, height: 20 }} />
          ) : offer.isBackOrder ? (
            t('app.common.offer_type.backorder')
          ) : change?.deliveryDateChanged && oldValues?.date ? (
            <OverlayTrigger
              trigger={['focus', 'hover']}
              placement='bottom'
              overlay={
                <Popover className='py-1 px-2'>
                  <Trans
                    i18nKey='app.purchase.redistribution.cart.errors.invalid_date'
                    values={{
                      date: locale.formatters.date(new Date(oldValues?.date), {
                        format: DateUtils.DATE_FORMAT.DM,
                      }),
                    }}
                    components={[
                      <Text
                        key={0}
                        heading='xs'
                        color='secondary'
                        weight='semi-bold'
                        component='div'
                      />,
                      <Text
                        key={1}
                        heading='xs'
                        color='secondary'
                        weight='semi-bold'
                        component='div'
                      />,
                    ]}
                  />
                </Popover>
              }
            >
              <Badge bg='warning' className='text-nowrap'>
                <Flex direction='row'>
                  <Icon icon='alert' />
                  <Text
                    heading='sm'
                    weight='bold'
                    color='warning'
                    className='d-flex align-items-center'
                  >
                    <FormatDate
                      date={change?.actual.deliveryDate}
                      format={DateUtils.DATE_FORMAT.DM}
                      locale={locale.locale}
                    />
                  </Text>
                </Flex>
              </Badge>
            </OverlayTrigger>
          ) : (
            <div className='d-inline-flex align-items-center gap-1'>
              <FormatDate
                date={offer.deliveryDate}
                format={DateUtils.DATE_FORMAT.DM}
                locale={locale.locale}
              />
              {offer.directDelivery && (
                <OverlayTrigger
                  overlay={
                    <Popover className='py-1 px-2'>
                      <Trans i18nKey='app.purchase.redistribution.pending_order.direct_delivery' />
                    </Popover>
                  }
                >
                  <Icon icon='info-circle' />
                </OverlayTrigger>
              )}
            </div>
          )}
        </Text>
        {offer.isJ0 && (
          <>
            <Icon icon='timer-flash' size='xs' />{' '}
            <Text heading='xxs' color='white'>
              <Trans i18nKey='app.purchase.table.express' />
            </Text>
          </>
        )}
      </td>
      <td className='bg-white border-0 pt-4'>
        <Text
          heading='normal'
          weight='semi-bold'
          className='text-center bg-white'
          id={`available-quantity-${offerIndex}-${supplier.id}`}
        >
          {isProcessing ? (
            <Spinner style={{ width: 20, height: 20 }} />
          ) : change?.quantityChanged ? (
            <OverlayTrigger
              trigger={['focus', 'hover']}
              placement='bottom'
              overlay={
                <Popover className='py-1 px-2'>
                  <Trans
                    i18nKey='app.purchase.redistribution.cart.errors.invalid_stock'
                    values={{
                      quantity: oldValues?.quantity,
                    }}
                    components={[
                      <Text
                        key={0}
                        heading='xs'
                        color='secondary'
                        weight='semi-bold'
                        component='div'
                      />,
                      <Text
                        key={1}
                        heading='xs'
                        color='secondary'
                        weight='semi-bold'
                        component='div'
                      />,
                    ]}
                  />
                </Popover>
              }
            >
              <Text component='span' color='danger'>
                <Icon
                  icon='error-warning-line'
                  height={26}
                  width={24}
                  style={{ paddingBottom: '5px' }}
                />
                {change?.actual?.availableQuantity}
              </Text>
            </OverlayTrigger>
          ) : (
            <>
              {isPartiallyAvailable && (
                <Text component='span' color='warning'>
                  <Icon icon='error-warning-line' />
                </Text>
              )}{' '}
              {offer.availableQuantity}
            </>
          )}
        </Text>
      </td>
      <td className='bg-white border-0 pt-4'>
        <div className='w-full' style={{ paddingLeft: '20%' }}>
          {offer.availableQuantity ? (
            <NumberField
              id={`quantity-${supplier.name}-${offerIndex}`}
              name={`items.${offerIndex}.quantity`}
              size='sm'
              min={MIN_QUANTITY}
              max={
                change?.quantityChanged
                  ? change?.actual?.availableQuantity
                  : offer.availableQuantity
              }
              disabled={order.parentStockOrder}
            />
          ) : (
            <NumberInput
              disabled
              name={`order.quantity`}
              size='sm'
              min={0}
              max={0}
            />
          )}
        </div>
      </td>
      <td className='bg-white border-0 pt-4'>
        <Text
          heading='normal'
          weight='semi-bold'
          className='text-center bg-white'
          id={`price-c-${offerIndex}-${supplier.id}`}
        >
          {locale.formatters.money(offer.redistributionBuyingPrice, {
            fallback: '-',
            currency: offer.currencyIsoCode ?? undefined,
          })}
        </Text>
      </td>
      <td className='bg-white border-O pt-4'>
        <Text
          heading='normal'
          weight='semi-bold'
          className='text-center bg-white'
          id={`price-r-${offerIndex}-${supplier.id}`}
        >
          {isProcessing ? (
            <Spinner style={{ width: 20, height: 20 }} />
          ) : change?.priceChanged ? (
            <OverlayTrigger
              trigger={['focus', 'hover']}
              placement='bottom'
              overlay={
                <Popover className='py-1 px-2'>
                  <Trans
                    i18nKey='app.purchase.redistribution.cart.errors.invalid_price'
                    values={{
                      price: oldValues?.sellingPrice,
                    }}
                    components={[
                      <Text
                        key={0}
                        heading='xs'
                        color='secondary'
                        weight='semi-bold'
                        component='div'
                      />,
                      <Text
                        key={1}
                        heading='xs'
                        color='secondary'
                        weight='semi-bold'
                        component='div'
                      />,
                    ]}
                  />
                </Popover>
              }
            >
              <Badge bg='warning' className='text-nowrap'>
                <Flex direction='row'>
                  <Text
                    heading='sm'
                    weight='bold'
                    color='warning'
                    className='d-flex align-items-center'
                  >
                    <Icon icon='alert' />
                    {locale.formatters.money(change?.actual?.sellingPrice, {
                      fallback: '-',
                      currency: order.currency,
                    })}
                  </Text>
                </Flex>
              </Badge>
            </OverlayTrigger>
          ) : (
            locale.formatters.money(offer.redistributionSellingPrice, {
              fallback: '-',
              currency: offer.currencyIsoCode,
            })
          )}
        </Text>
      </td>
    </tr>
  );
}

export default SuggestedTyreRow;
